import { forwardRef, useImperativeHandle, useState } from 'react'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SmartSelect } from '../../generic/smartSelect'
import AlertUI from '../../generic/AlertUI'

const variables = ['x', 'y', 'z', 'a', 'b', 'c']

const emptyVariableValues = () => {
  const values = {}
  variables.forEach((variable) => {
    values[variable] = { nominal: '', offset: '0' }
  })
  return values
}

const PartPresetForm = forwardRef(({ part, onClose, onSaved }, ref) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [selectedFixture, setSelectedFixture] = useState(null)
  const [variableValues, setVariableValues] = useState(emptyVariableValues())
  const [presetNumber, setPresetNumber] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // numeroPreset (machine preset number, stored outside our context) is mutually exclusive with
  // the nominal/offset values: if a preset number is set, the normal values must not be used.
  const usePresetNumber = presetNumber !== ''
  const matrixHasValues = variables.some((variable) => {
    const { nominal, offset } = variableValues[variable]
    return nominal !== '' || (offset !== '' && parseFloat(offset) !== 0)
  })

  // Server-side paginated search for the fixture selector. Returns { id, code } so the
  // displayed label is the fixture code.
  const searchFixtures = async (search) => {
    const pagination = {
      columnFilters: ['code'],
      columnFilterValues: [search || ''],
      sortKey: ['code'],
      sortMode: [1],
      rows: [],
      page: 0,
      size: 10,
      allSelected: false
    }
    const data = await apiGet('fixtureInstancesByPartReferencePagination/' + part.partReference.id, pagination)
    return (data?.content || []).map((fixture) => ({ id: fixture.id, code: fixture.code }))
  }

  const handleVariableChange = (variable, field, value) => {
    setVariableValues((previous) => ({
      ...previous,
      [variable]: { ...previous[variable], [field]: value }
    }))
  }

  const handleFixtureChange = async (fixture) => {
    setSelectedFixture(fixture || null)
    if (!fixture) return
    const data = await apiGet('fixtureReferencePartReferencePreset/' + part.partReference.id + '/' + fixture.id)
    setVariableValues((previous) => {
      const next = { ...previous }
      variables.forEach((variable) => {
        const value = data[variable] !== undefined && data[variable] !== null ? data[variable] : ''
        next[variable] = { ...next[variable], nominal: String(value) }
      })
      return next
    })
  }

  const validateOffset = (variable) => {
    const { nominal, offset } = variableValues[variable]
    if (offset === '') {
      if (nominal === '') return ''
      return t_('Offset should not be empty')
    }
    if (nominal === '' && parseFloat(offset) !== 0) {
      return t_('Nominal should not be empty')
    }
    return ''
  }

  const buildPayload = () => {
    const payload = {
      part: { id: part.id, code: part.code, name: part.name },
      fixture: selectedFixture,
      presetNumber: usePresetNumber ? presetNumber : null
    }
    variables.forEach((variable) => {
      const { nominal, offset } = variableValues[variable]
      payload[variable + '_Nominal'] = usePresetNumber ? null : nominal
      payload[variable + '_Offset'] = usePresetNumber ? null : (offset === '' ? 0 : offset)
    })
    return payload
  }

  const handleSave = async () => {
    if (isSubmitting) return
    if (!selectedFixture) {
      showAlert({ severity: 'error', title: t_('Fixture') + ': ' + t_('This field is mandatory') })
      return
    }
    if (!usePresetNumber) {
      for (const variable of variables) {
        const error = validateOffset(variable)
        if (error) {
          showAlert({ severity: 'error', title: variable.toUpperCase() + ': ' + error })
          return
        }
      }
    }

    try {
      setIsSubmitting(true)
      await apiPost('fixturePartPreset', buildPayload())
      if (onSaved && typeof onSaved === 'function') onSaved()
      if (onClose && typeof onClose === 'function') onClose()
    } catch (error) {
      showAlert({ severity: 'error', title: error.message })
    } finally {
      setIsSubmitting(false)
    }
  }

  useImperativeHandle(ref, () => ({ submit: handleSave }))

  return (
    <Box sx={{ minWidth: '520px', padding: '1rem' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 9999 }}>{AlertElement}</div>
      <Box sx={{ marginBottom: '1.5rem' }}>
        <SmartSelect
          label={t_('Fixture')}
          value={selectedFixture}
          sourceWithSearch={searchFixtures}
          onChange={handleFixtureChange}
          autoSelectFirst={false}
          required
          width='100%'
        />
      </Box>
      <Box sx={{ marginBottom: '1.5rem' }}>
        <TextField
          type='number'
          size='small'
          fullWidth
          label={t_('Preset number')}
          value={presetNumber}
          onChange={(event) => setPresetNumber(event.target.value)}
          disabled={matrixHasValues}
          helperText={t_('If set, the machine preset is used and tolerance values are ignored')}
        />
      </Box>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell align='center'>{t_('Nominal')}</TableCell>
            <TableCell align='center'>{t_('Offset')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {variables.map((variable) => {
            const error = validateOffset(variable)
            return (
              <TableRow key={variable}>
                <TableCell sx={{ verticalAlign: 'top', paddingTop: '1.25rem' }}>
                  <Typography variant='body2'>{variable.toUpperCase()}</Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    type='number'
                    size='small'
                    disabled={usePresetNumber}
                    value={variableValues[variable].nominal}
                    onChange={(event) => handleVariableChange(variable, 'nominal', event.target.value)}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>
                  <TextField
                    type='number'
                    size='small'
                    disabled={usePresetNumber}
                    value={variableValues[variable].offset}
                    onChange={(event) => handleVariableChange(variable, 'offset', event.target.value)}
                    error={!!error}
                    helperText={error}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
})

PartPresetForm.displayName = 'PartPresetForm'

export default PartPresetForm
